/**
 * A user's SimTeach puzzle progress (issue #4675): a per-puzzle summary map
 * and one record per attempt. Written only by simteach at session teardown
 * (`save_puzzle_attempt` in `src/apps/simteach/app/firebase_session.py`,
 * shapes built in `section_teaching/puzzle_attempt.py`); clients read them.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../firebase/types';
import {
  SIMTEACH_PUZZLES_COLLECTION,
  SimTeachPuzzleKindSchema,
  SimTeachPuzzleSchema,
} from './simTeachPuzzle';

/** The subcollection of the summary doc holding one doc per attempt. */
export const SIMTEACH_PUZZLE_ATTEMPTS_COLLECTION = 'simTeachPuzzleAttempts';

/** How an attempt ended: a met goal (puzzle) or every card done (lesson) is completed. */
export const SimTeachPuzzleOutcomeSchema = z.enum([
  'completed',
  'failed',
  'incomplete',
]);

/** How an attempt ended. */
export type SimTeachPuzzleOutcome = z.infer<typeof SimTeachPuzzleOutcomeSchema>;

/** The subcollection of the summary doc holding one debrief per attempt, by session id. */
export const SIMTEACH_PUZZLE_DEBRIEFS_COLLECTION = 'simTeachPuzzleDebriefs';

/**
 * A puzzle's challenge: `offered` by the latest debrief, `completed` once a
 * replay met it, or `none` when the latest completion has no challenge.
 */
export const SimTeachPuzzleChallengeEntrySchema = z.object({
  /** Where the challenge stands. */
  status: z.enum(['offered', 'completed', 'none']),
  /** The attempt whose debrief offered it; omitted for `none`. */
  fromSessionId: z.string().optional(),
  /** The step it targets (Elicit, Confront, or Resolve); omitted for `none`. */
  step: z.string().optional(),
  /** The move it asks for; omitted for `none`. */
  move: z.string().optional(),
  /** The challenge itself, as a goal; omitted for `none`. */
  goal: z.string().optional(),
  /** When a replay met it; omitted until then. */
  completedAt: FirestoreTimestampSchema.optional(),
  /** The latest replay that took it, met or not; omitted until one has. */
  lastTrySessionId: z.string().optional(),
});

/** A puzzle's challenge. */
export type SimTeachPuzzleChallengeEntry = z.infer<
  typeof SimTeachPuzzleChallengeEntrySchema
>;

/** One puzzle's entry in a user's summary map. */
export const SimTeachPuzzleProgressEntrySchema = z.object({
  /** Attempts saved for this puzzle. */
  attempts: z.number().int().nonnegative(),
  /** The first completed attempt's end; null until one completes. */
  completedAt: FirestoreTimestampSchema.nullable(),
  /** The latest attempt's end. */
  lastAttemptAt: FirestoreTimestampSchema,
  /** The latest attempt's outcome. */
  lastOutcome: SimTeachPuzzleOutcomeSchema,
  /** The latest attempt's id in the attempts subcollection. */
  lastSessionId: z.string(),
  /** The attempt whose debrief is the puzzle's latest feedback; omitted before one. */
  lastDebriefSessionId: z.string().optional(),
  /**
   * The puzzle's challenge. Every completion since challenges began leaves
   * one, so a completed entry without it is from before then, which counts
   * as mastered (see `isPuzzleMastered`).
   */
  challenge: SimTeachPuzzleChallengeEntrySchema.optional(),
  /** The first time a replay met the puzzle's challenge; omitted until then. */
  masteredAt: FirestoreTimestampSchema.optional(),
});

/** One puzzle's entry in a user's summary map. */
export type SimTeachPuzzleProgressEntry = z.infer<
  typeof SimTeachPuzzleProgressEntrySchema
>;

/**
 * Whether a puzzle is mastered: a replay met its challenge, or it was
 * completed before challenges existed (no `challenge` entry).
 *
 * @param entry - The puzzle's summary entry.
 * @return True for a mastered puzzle.
 * @throws Never.
 */
export function isPuzzleMastered(entry: SimTeachPuzzleProgressEntry): boolean {
  return (
    entry.masteredAt !== undefined ||
    (entry.completedAt !== null && entry.challenge === undefined)
  );
}

/** A user's summary map: one entry per attempted puzzle, keyed by puzzle id. */
export const SimTeachPuzzleProgressSchema = z.record(
  z.string(),
  SimTeachPuzzleProgressEntrySchema,
);

/** A user's summary map, keyed by puzzle id. */
export type SimTeachPuzzleProgress = z.infer<
  typeof SimTeachPuzzleProgressSchema
>;

/** A check that failed or returned nothing. */
const CheckErrorSchema = z.object({
  /** What went wrong. */
  error: z.string(),
  /** When it landed. */
  at: FirestoreTimestampSchema,
});

/** The move check's result on a teacher pause. */
const MovesCheckSchema = z.union([
  z.object({
    /** The teaching moment the pause was at. */
    moment: z.string(),
    /** The good moves the teacher made; empty for none. */
    movesUsed: z.array(z.string()),
    /** The moment's violation, if committed; "" for none. */
    violation: z.string(),
    /** The teacher's words behind it. */
    evidence: z.string(),
    /** One sentence on why. */
    reason: z.string(),
    /** When it landed. */
    at: FirestoreTimestampSchema,
  }),
  z.object({
    /** A later line merged this one's turn; its check moved there. */
    supersededBy: z.string(),
    /** When it was superseded. */
    at: FirestoreTimestampSchema,
  }),
  CheckErrorSchema,
]);

/** The card check's verdicts on a teacher pause. */
const CardsCheckSchema = z.union([
  z.object({
    /** One verdict per evaluated card. */
    verdicts: z.array(
      z.object({
        /** The card's id. */
        cardId: z.string(),
        /** The verdict: pending, done, or failed. */
        status: z.string(),
        /** The words behind it. */
        evidence: z.string(),
        /** One sentence on why. Omitted on records saved before it existed. */
        reason: z.string().optional(),
        /** For failed, or an incomplete attempt: what to say or add instead. Omitted on older records. */
        suggestion: z.string().optional(),
        /** For pending: a clear but incomplete attempt at the card. Omitted on older records. */
        attempted: z.boolean().optional(),
      }),
    ),
    /** When it landed. */
    at: FirestoreTimestampSchema,
  }),
  CheckErrorSchema,
]);

/** The goal check prompted by a line. */
const GoalCheckSchema = z.union([
  z.object({
    /** not_met, met, or failed; met and failed settle the goal. */
    status: z.enum(['not_met', 'met', 'failed']),
    /** The lines that show it. */
    evidence: z.string(),
    /** One sentence on why, or what is still missing. */
    reason: z.string(),
    /** For failed: what to do instead. Omitted on older records. */
    suggestion: z.string().optional(),
    /** When it landed. */
    at: FirestoreTimestampSchema,
  }),
  CheckErrorSchema,
]);

/** The failure that ended an attempt. */
export const SimTeachPuzzleFailureSchema = z.object({
  /** A failed card, a surprise that timed out, or a broken goal. */
  kind: z.enum(['card', 'timeout', 'goal']),
  /** The card that failed; null for the goal. */
  card_id: z.string().nullable(),
  /** That card's title; null for the goal. */
  title: z.string().nullable(),
  /** The quoted line behind it; "" for a timeout. */
  evidence: z.string(),
  /** One sentence on why. */
  reason: z.string(),
  /** What to do instead; "" when none. */
  suggestion: z.string(),
  /**
   * The first teacher line of the turn behind it, which a retry starts
   * before; null or omitted when no teacher turn caused it.
   */
  turn_id: z.string().nullable().optional(),
});

/** The failure that ended an attempt. */
export type SimTeachPuzzleFailure = z.infer<typeof SimTeachPuzzleFailureSchema>;

/** One line of an attempt's transcript, with the checks run on it. */
export const SimTeachPuzzleTranscriptLineSchema = z.object({
  /** Position, ordered by `at`. */
  seq: z.number().int().nonnegative(),
  /** When the line was said. */
  at: FirestoreTimestampSchema,
  /** Who said it; `room` is the room falling silent. */
  speaker: z.enum(['teacher', 'student', 'room']),
  /** What was said. */
  text: z.string(),
  /**
   * Replayed from the opening, said live, or said live in the earlier
   * attempt a retry started from.
   */
  source: z.enum(['opening', 'live', 'resumed']),
  /** A teacher line's pause, or the pause a student line answers. */
  turnId: z.string().optional(),
  /** A student line's seat. */
  studentId: z.number().int().optional(),
  /** A student line's first name. */
  name: z.string().optional(),
  /** Each check run on this line, at most once per kind. */
  checks: z.object({
    /** Move check; live teacher pauses only. */
    moves: MovesCheckSchema.optional(),
    /** Card check; teacher pauses with open cards only. */
    cards: CardsCheckSchema.optional(),
    /** Goal check this line prompted. */
    goal: GoalCheckSchema.optional(),
  }),
});

/** One line of an attempt's transcript. */
export type SimTeachPuzzleTranscriptLine = z.infer<
  typeof SimTeachPuzzleTranscriptLineSchema
>;

/**
 * A thinking student's mind once it had thought about every line up to
 * `through`; mirrors `MindLog` in simteach's puzzle_attempt.py.
 */
export const SimTeachPuzzleMindSchema = z.object({
  /** The student's seat. */
  studentId: z.number().int(),
  /** When the newest line this mind accounts for was said. */
  through: FirestoreTimestampSchema,
  /** The student's thoughts, oldest first. */
  beliefs: z.array(
    z.object({
      /** The thought. */
      text: z.string(),
      /** How firmly the student holds it, from 0 to 1. */
      confidence: z.number().min(0).max(1),
      /** The thought's significance score. */
      importance: z.number(),
    }),
  ),
  /** The student's mood. */
  mood: z.string(),
  /** What the student is trying to do. */
  goal: z.string(),
});

/** A thinking student's mind at one moment of an attempt. */
export type SimTeachPuzzleMind = z.infer<typeof SimTeachPuzzleMindSchema>;

/** One attempt at a SimTeach puzzle. */
export const SimTeachPuzzleAttemptSchema = z.object({
  /** The session's id; also this doc's id. */
  sessionId: z.string(),
  /** The teacher's uid. */
  uid: z.string(),
  /** The course the puzzle belongs to. */
  courseId: z.string(),
  /** The puzzle played. */
  puzzleId: z.string(),
  /** Lesson or puzzle. */
  kind: SimTeachPuzzleKindSchema,
  /** The puzzle's definition as played; admins may edit it later. */
  puzzle: SimTeachPuzzleSchema,
  /** When the session started. */
  startedAt: FirestoreTimestampSchema,
  /** When it ended. */
  endedAt: FirestoreTimestampSchema,
  /** How it ended. */
  outcome: SimTeachPuzzleOutcomeSchema,
  /** The goal as it ended; puzzles with a goal only. */
  goal: z
    .object({
      /** open, met, or failed. */
      status: z.enum(['open', 'met', 'failed']),
      /** The lines behind the settled status; "" while open. */
      evidence: z.string(),
      /** When it settled; null while open. */
      settledAt: FirestoreTimestampSchema.nullable(),
    })
    .optional(),
  /** Every card as it ended; empty for puzzles. */
  cards: z.array(
    z.object({
      /** The card's id. */
      id: z.string(),
      /** pending, done, or failed. */
      status: z.enum(['pending', 'done', 'failed']),
      /** The words behind the settled status. */
      evidence: z.string(),
      /** What settled it; null while pending. */
      source: z.enum(['evaluator', 'override', 'timeout']).nullable(),
      /** When it settled; null while pending. */
      settledAt: FirestoreTimestampSchema.nullable(),
    }),
  ),
  /** The first failure, which ended the attempt; null or omitted when none. */
  failure: SimTeachPuzzleFailureSchema.nullable().optional(),
  /** Where a retry started; omitted for a fresh attempt. */
  resumedFrom: z
    .object({
      /** The earlier attempt's session id. */
      sessionId: z.string(),
      /** The teacher line the retry started just before. */
      turnId: z.string(),
    })
    .optional(),
  /** Every line of the session, oldest first. */
  transcript: z.array(SimTeachPuzzleTranscriptLineSchema),
  /**
   * Each thinking student's mind every time it changed, oldest first,
   * including those a retry carried over; omitted when there are none.
   */
  minds: z.array(SimTeachPuzzleMindSchema).optional(),
});

/** One attempt at a SimTeach puzzle. */
export type SimTeachPuzzleAttempt = z.infer<typeof SimTeachPuzzleAttemptSchema>;

/**
 * Path of a user's SimTeach puzzle summary doc.
 *
 * @param args.uid - The user.
 * @param args.courseId - The course the puzzles belong to.
 * @return `users/{uid}/{courseId}/simTeachPuzzles`.
 * @throws Never.
 */
export function simTeachPuzzleProgressDocPath({
  uid,
  courseId,
}: {
  uid: string;
  courseId: string;
}): string {
  return `users/${uid}/${courseId}/${SIMTEACH_PUZZLES_COLLECTION}`;
}

/**
 * Path of one SimTeach puzzle attempt doc.
 *
 * @param args.uid - The user.
 * @param args.courseId - The course the puzzle belongs to.
 * @param args.sessionId - The attempt's session id.
 * @return `users/{uid}/{courseId}/simTeachPuzzles/simTeachPuzzleAttempts/{sessionId}`.
 * @throws Never.
 */
export function simTeachPuzzleAttemptDocPath({
  uid,
  courseId,
  sessionId,
}: {
  uid: string;
  courseId: string;
  sessionId: string;
}): string {
  return `${simTeachPuzzleProgressDocPath({uid, courseId})}/${SIMTEACH_PUZZLE_ATTEMPTS_COLLECTION}/${sessionId}`;
}

/**
 * Path of one SimTeach puzzle attempt's debrief doc.
 *
 * @param args.uid - The user.
 * @param args.courseId - The course the puzzle belongs to.
 * @param args.sessionId - The attempt's session id.
 * @return `users/{uid}/{courseId}/simTeachPuzzles/simTeachPuzzleDebriefs/{sessionId}`.
 * @throws Never.
 */
export function simTeachPuzzleDebriefDocPath({
  uid,
  courseId,
  sessionId,
}: {
  uid: string;
  courseId: string;
  sessionId: string;
}): string {
  return `${simTeachPuzzleProgressDocPath({uid, courseId})}/${SIMTEACH_PUZZLE_DEBRIEFS_COLLECTION}/${sessionId}`;
}
